import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOSchema from '../components/SEOSchema';
import { ShieldAlert, AlertTriangle, Stethoscope, Phone } from 'lucide-react';

const sections = [
  {
    title: 'Educational & Informational Use Only',
    body: 'All content, programs, calculators, the Tcube Full Body Health Analysis, blog articles and guidance provided by TCUBE FITNESS™ are for general educational and informational purposes only. They do not constitute medical advice, diagnosis, or treatment.',
  },
  {
    title: 'Not a Substitute for Medical Care',
    body: 'Our doctor-guided nutrition and fitness coaching complements, but does not replace, care from your treating physician, gynecologist, or specialist. Always seek the advice of a qualified healthcare professional regarding any medical condition, medication, or symptom, and never disregard professional medical advice because of something you read here.',
  },
  {
    title: 'No Doctor–Patient Relationship',
    body: 'Using this website, our calculators, or submitting a health analysis does not create a doctor–patient relationship. Coaching engagements are lifestyle and nutrition services delivered with medical oversight, not the practice of medicine, diagnosis, or prescription.',
  },
  {
    title: 'Individual Results Vary',
    body: 'Health outcomes depend on many individual factors. We make no guarantees of specific results. Recommendations are general and may not be appropriate for your particular circumstances.',
  },
  {
    title: 'Pregnancy, Conditions & Medications',
    body: 'If you are pregnant, trying to conceive, breastfeeding, managing a medical condition, or taking medication, consult your treating doctor before making any dietary, supplement, or exercise changes. Continue all routine medical care.',
  },
  {
    title: 'Tools & Calculators',
    body: 'Calculators and the health analysis provide estimates based on the data you enter. They are not diagnostic tools and should not be used to make medical decisions on their own.',
  },
];

const MedicalDisclaimerPage = () => (
  <div className="min-h-screen bg-slate-950">
    <Helmet>
      <title>Medical Disclaimer | TCUBE FITNESS™</title>
      <meta name="description" content="Medical disclaimer for TCUBE FITNESS. Our doctor-guided nutrition and fitness coaching is for educational purposes and does not replace medical care." />
      <link rel="canonical" href="https://tcubefitness.com/medical-disclaimer" />
    </Helmet>

    <SEOSchema
      type="webpage"
      name="Medical Disclaimer | TCUBE FITNESS™"
      description="Medical disclaimer for TCUBE FITNESS doctor-guided nutrition and fitness coaching."
      url="/medical-disclaimer"
      breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Medical Disclaimer', path: '/medical-disclaimer' }]}
    />

    <Header />

    <main id="main-content" className="pt-28 sm:pt-32 pb-20" data-testid="medical-disclaimer-page">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium mb-6">
            <ShieldAlert className="w-4 h-4" aria-hidden="true" />
            Important Health Information
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Medical Disclaimer
          </h1>
          <p className="text-slate-400 text-base sm:text-lg mb-10">
            Please read this carefully. By using this website and our services, you acknowledge and agree to the following.
          </p>

          <div className="space-y-5">
            {sections.map((s, i) => (
              <div key={i} className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6" data-testid={`disclaimer-section-${i}`}>
                <h2 className="text-lg sm:text-xl font-semibold text-white mb-2 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-blue-400 flex-shrink-0" aria-hidden="true" />
                  {s.title}
                </h2>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-rose-500/5 border border-rose-500/25 rounded-2xl p-6 flex gap-3 items-start" data-testid="emergency-notice">
            <AlertTriangle className="w-6 h-6 text-rose-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <h3 className="text-rose-300 font-semibold mb-1">In a medical emergency</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                TCUBE FITNESS does not provide emergency or acute medical care. If you experience a medical emergency,
                contact your local emergency services or visit the nearest hospital immediately.
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3 text-sm">
            <Link to="/privacy-policy" className="text-blue-400 hover:underline">Privacy Policy</Link>
            <span className="text-slate-700">|</span>
            <Link to="/terms" className="text-blue-400 hover:underline">Terms &amp; Conditions</Link>
            <span className="text-slate-700">|</span>
            <Link to="/refund-policy" className="text-blue-400 hover:underline">Refund Policy</Link>
            <span className="text-slate-700">|</span>
            <a href="https://wa.me/919515901777" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:underline inline-flex items-center gap-1">
              <Phone className="w-4 h-4" aria-hidden="true" /> Talk to our team
            </a>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

export default MedicalDisclaimerPage;
